import react, { useState, createContext, FC, useContext, useEffect, useRef, useMemo } from 'react';
import _, { clone } from 'lodash';
import dayjs from 'dayjs';
import { UserContext } from './ConfigContext';
import { extractEmailAndName, getConfig, reverseObjectKeyValues, sendNotification } from 'utils/helperMethods';
import {
    useFetchKpiMetricsValues,
    useFetchRecommendationValues,
    useFetchScenarioNames,
    useFetchSimulatorInput,
    useFetchSimulatorOutput,
    useFetchPorfolioUpdateKpis,
    useFetchScenario,
    useSaveScenario,
    useFetchPortfolioUpdateChangesAppliedKpis,
} from 'hooks/priceSimulator';
import { FilterContextData } from './FilterContext';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
    processInputVariablesForOutputCal,
    reversePortfolioUpdateKpiParser,
} from 'parser/priceSimulator/priceSimulatorDataParser';
import { ConfigKeys, PriceSimulatorSteps } from 'utils/Enums';
import { trackEventMixpanel } from 'utils/mixpanel';
import {
    PSR_NAVIGATION_BUTTON_CLICKED,
    PSR_PROTFOLIO_SUBMIT_BTN_CLICKED as PSR_PORTFOLIO_SUBMIT_BTN_CLICKED,
    PSR_SIMULATION_BUTTON_CLICKED,
} from 'utils/mixpanelEventNames';

const priceSimulatorDataContext = createContext<{
    viewMode: boolean;
    createSimulatorRef: any;
    currentStep: number;
    nextStepHandler: (val: number) => void;
    scenarioId: number;

    // step 1
    title: string; // store scenario name
    setTitle: Function; // updadte scenario name
    selectedKpiValues: number[]; // to maintain selected kpi's from the available list
    setSelectedKpiValues: (items: number[]) => void; // to update the selected kpi's
    selectedDataSource: any;
    setSelectedDataSource: Function;
    defaultKpiMetrics: number[]; // on reset we will enable default metrics for the user
    kpiMetrictsState: Array<KpiMetricType>; // kpi metrics state
    setKpiMetrictsState: (values: Array<KpiMetricType>) => void; // udpate kpi metric state
    simulationDateRange: any; // to store the simualation date range
    setSimulationDateRange: Function; // update the simulation date range
    simulationTimePeriodSelection: any; // to store simulation period selection
    setSimulationTimePeriodSelection: Function; // update the simulation period selection
    priceEffectiveDate: any; // to store simulation price effective date
    setPriceEffectiveDate: Function; // to update price effective date
    tempPriceEffectiveDate: any; // to store simulation price effective date
    setTempPriceEffectiveDate: Function; // to update price effective date
    levels: Array<string>; // levels which current tenant supports, read from config

    // step 2
    expandedRowKeys: Array<number>;
    setExpandedRowKeys: Function;
    expandedRowDetails: { columnName: string; rowName: any; rowIndex: number };
    setExpandedRowDetails: Function;
    addNewPPGDetails: { columnName: string; rowName: any; rowIndex: number };
    setAddNewPPGDetails: Function;
    portfolioUpdateKpisData: any;
    setPortfolioUpdateKpisData: Function;
    portfolioUpdateKpisLoading: boolean;
    changedPortfolioUpdateKpisLoading: boolean;
    portfolioUpdateSubmitClick: Function;
    hasPortfolioKpisUpdated: boolean; // boolean to check if user had made any changes to portfolio update kpis

    // step 3
    simulationInputDataState: Array<any>; // simulation data will be sent as variables for output
    setSimulationInputDataState: Function; // update function
    simulationOutputDataState: Array<any>; // output data we get from apis
    setSimulationOutputDataState: Function; // update function
    simulationRecommendationDataState: Array<any>; // output data we get from apis
    setSimulationRecommendationDataState: Function; // update function

    // hooks
    kpiMetricsLoading: boolean; // kpi metrics loading state
    kpiMetricsError: string | null; // kpi metrics error state
    fetchKpitMetrics: (variables: any) => void; // kpi metric gql call

    simulationInputLoading: boolean; // gql loading for input api
    simulationInputData: any; // gql data for input api
    simulationInputError: string | null; // gql error for input api
    fetchSimulatorInput: (variables: any) => void; // gql api call

    simulationOutputLoading: boolean; // gql loading for output api
    simulationOutputData: any; // gql data for output api
    simulationOutputError: string | null; // gql error for output api
    fetchSimulatorOutput: (variables: any) => void; // gql api call
    scenarioNamesData: Array<string> | null; // gql api call
    globalFilters: FilterSelectionProps[]; // global filter variables
    setGlobalFilters: Function; // to update global filter variables
    fetchScenario: Function; // to fetch single scenario details
    fetchScenarioLoading: boolean; // fetch scenario loading
    displayTile: any; //opportunity identification tile button
    setDisplayTile: Function; //to update opportunity identification tile button
    saveScenarioLoading: boolean; // gql loading for save scenario api
    simulationRecomLoading: boolean; // gql loading for recommendation scenario api

    newPortfolioUpdateKpis: any;
    setNewPortfolioUpdateKpis: Function;
}>({
    viewMode: false,
    createSimulatorRef: null,
    currentStep: 0,
    scenarioId: 0,
    nextStepHandler: () => {},
    title: '',
    setTitle: () => {},
    selectedKpiValues: [],
    setSelectedKpiValues: () => {},
    defaultKpiMetrics: [],
    kpiMetrictsState: [],
    setKpiMetrictsState: (values: any) => {},
    selectedDataSource: undefined,
    setSelectedDataSource: () => {},
    simulationDateRange: [],
    setSimulationDateRange: () => {},
    priceEffectiveDate: [null],
    setPriceEffectiveDate: () => {},
    tempPriceEffectiveDate: [null],
    setTempPriceEffectiveDate: () => {},
    kpiMetricsLoading: false,
    kpiMetricsError: null,
    fetchKpitMetrics: () => {},
    simulationInputLoading: false,
    simulationInputData: undefined,
    simulationInputError: null,
    fetchSimulatorInput: () => {},
    levels: [],
    simulationInputDataState: [],
    setSimulationInputDataState: () => {},
    simulationOutputLoading: false,
    simulationOutputData: undefined,
    simulationOutputError: null,
    fetchSimulatorOutput: () => {},
    simulationOutputDataState: [],
    setSimulationOutputDataState: () => {},
    simulationRecommendationDataState: [],
    setSimulationRecommendationDataState: () => {},
    simulationTimePeriodSelection: null,
    setSimulationTimePeriodSelection: () => {},
    scenarioNamesData: null,
    globalFilters: [],
    setGlobalFilters: () => {},
    fetchScenario: () => {},
    fetchScenarioLoading: false,
    displayTile: {},
    setDisplayTile: () => {},
    saveScenarioLoading: false,
    simulationRecomLoading: false,
    portfolioUpdateKpisData: null,
    setPortfolioUpdateKpisData: () => {},
    portfolioUpdateKpisLoading: false,
    changedPortfolioUpdateKpisLoading: false,
    newPortfolioUpdateKpis: undefined,
    setNewPortfolioUpdateKpis: () => {},
    expandedRowKeys: [],
    setExpandedRowKeys: () => {},
    expandedRowDetails: { columnName: '', rowName: '', rowIndex: -1 },
    setExpandedRowDetails: () => {},
    addNewPPGDetails: { columnName: '', rowName: '', rowIndex: -1 },
    setAddNewPPGDetails: () => {},
    portfolioUpdateSubmitClick: () => {},
    hasPortfolioKpisUpdated: false,
});
interface props {
    children: React.ReactNode;
}

export type FilterSelectionProps = {
    title: string;
    selectedItems: string[];
    hideEditIcon?: boolean;
};

export type KpiMetricType = {
    key: number;
    value: string;
    name: string;
    actualValue: string; // i.e actualValue
    goalValue: string;
    percentChange: string;
    selected: string;
    simulatedValue: string;
    pre: string;
    post: string;
    decimalToRoundOff?: number;
};

function setKpiMetricsWithAllProperties(kpiMetrics: any, defaultKpiMetrics: any) {
    const newKpiMetrics: KpiMetricType[] = kpiMetrics?.map((kpi: any) => {
        return {
            ...kpi,
            actualValue: '-',
            goalValue: '',
            percentChange: '',
            selected: defaultKpiMetrics.includes(kpi.key),
            simulatedValue: '',
            actVsGoal: '',
            simVsGaol: '',
        };
    });
    return newKpiMetrics;
}

const PriceSimulatorContext: FC<props> = ({ children }) => {
    const { appConfig } = useContext(UserContext);
    const { applyFilterObject, allCheckedAttribute, allSelectedValueForAllAttribute } = useContext(FilterContextData);
    const {
        kpiMetrics,
        defaultKpiMetrics,
        levels,
        inputKeyMapping,
        addPortfolioUpdateKpis, // kpis for new one
        opportunityIdentificationTiles: tiles,
    } = getConfig(ConfigKeys.PRICE_SIMULATOR, appConfig);

    const [currentStep, setCurrentStep] = useState(1);
    const [scenarioId, setScenarioId] = useState<number>(0);
    const [createdByUser, setCreatedByUser] = useState<string>('');

    // set to true after fetch scenario api is called
    const [isEditFlowApiCalled, setIsEditFlowApiCalled] = useState(false);
    const [isEditFlowFiltersApplied, setIsEditFlowFiltersApplied] = useState(true);
    const [triggertFetchScenario, setTriggerFetchScenario] = useState(true);
    const navigate = useNavigate();

    const { id } = useParams();
    const location = useLocation();
    const isViewMode = () => location.pathname?.includes('view');
    const isCreateMode = () => location.pathname?.includes('new');
    const isEditMode = () => location.pathname?.includes('edit');

    // step 1
    const [selectedDataSource, setSelectedDataSource] = useState({ label: '', value: '' });
    const [simulationTimePeriodSelection, setSimulationTimePeriodSelection] = useState<any>();
    const [simulationDateRange, setSimulationDateRange] = useState<any>([null, null]);
    const [priceEffectiveDate, setPriceEffectiveDate] = useState<any>([null, null]);
    const [tempPriceEffectiveDate, setTempPriceEffectiveDate] = useState<any>([null]);
    const [displayTile, setDisplayTile] = useState(tiles[0]);
    const [title, setTitle] = useState(() => `Scenario Name ${Date.now()}`);
    const [selectedKpiValues, setSelectedKpiValues] = useState<number[]>(defaultKpiMetrics);
    const [globalFilters, setGlobalFilters] = useState<FilterSelectionProps[]>([]);

    const [kpiMetrictsState, setKpiMetrictsState] = useState<KpiMetricType[]>(
        setKpiMetricsWithAllProperties(kpiMetrics, defaultKpiMetrics),
    );
    const [newPortfolioUpdateKpis, setNewPortfolioUpdateKpis] = useState<KpiMetricType[]>(addPortfolioUpdateKpis);

    // step 2 - portfolio update
    const [portfolioUpdateKpisData, setPortfolioUpdateKpisData] = useState<Array<any>>([]);
    const [backUpPortfolioUpdateKpisData, setBackUpPortfolioUpdateKpisData] = useState<Array<any>>([]);
    const [expandedRowKeys, setExpandedRowKeys] = useState<Array<number>>([]);
    const [hasPortfolioKpisUpdated, setHasPortfolioKpisUpdated] = useState<boolean>(false);
    const [expandedRowDetails, setExpandedRowDetails] = useState({ columnName: '', rowName: '', rowIndex: -1 });
    const [addNewPPGDetails, setAddNewPPGDetails] = useState({ columnName: '', rowName: '', rowIndex: -1 });

    // step3 opportunity identify
    const [simulationInputDataState, setSimulationInputDataState] = useState<Array<any>>([]);
    const [simulationOutputDataState, setSimulationOutputDataState] = useState<Array<any>>([]);
    const [simulationRecommendationDataState, setSimulationRecommendationDataState] = useState<Array<any>>([]);

    const createSimulatorRef = useRef(false);

    // kpi metrics GQL
    const {
        loading: kpiMetricsLoading,
        data: kpiMetricsData,
        error: kpiMetricsError,
        fetchKpitMetrics,
    } = useFetchKpiMetricsValues();

    // scenario names GQL
    const {
        loading: scenarioNamsesLoading,
        data: scenarioNamesData,
        error: scenarioNamesError,
        fetchScenarioNames,
    } = useFetchScenarioNames();

    // fetch portfolio update kpi information
    const {
        loading: portfolioUpdateKpisLoading,
        data: gqlPortfolioUpdateKpis,
        fetchPortfolioUpdateKpis,
    } = useFetchPorfolioUpdateKpis();

    // fetch changed portfolio update kpi information
    const { loading: changedPortfolioUpdateKpisLoading, fetchPortfolioUpdateChangesAppliedKpis } =
        useFetchPortfolioUpdateChangesAppliedKpis({
            setKpiMetrictsState,
            setPortfolioUpdateKpisData,
            setBackUpPortfolioUpdateKpisData,
            kpiMetrictsState,
        });

    // simulation input price GQL
    const {
        loading: simulationInputLoading,
        data: simulationInputData,
        error: simulationInputError,
        fetchSimulatorInput,
    } = useFetchSimulatorInput();

    // simulation recommendation values GQL
    const {
        loading: simulationRecomLoading,
        data: recommendationData,
        error: simulationRecomError,
        fetchRecommendationValues,
    } = useFetchRecommendationValues();

    // simulation output price GQL
    const {
        loading: simulationOutputLoading,
        data: simulationOutputData,
        error: simulationOutputError,
        fetchSimulatorOutput,
    } = useFetchSimulatorOutput({ kpiMetrictsState, setKpiMetrictsState });

    // save scenario GQL
    const {
        loading: saveScenarioLoading,
        data: saveScenarioData,
        error: saveScenarioError,
        saveScenario,
    } = useSaveScenario({ setScenarioId });

    // fetch single scenario GQL
    const { loading: fetchScenarioLoading, fetchScenario } = useFetchScenario({
        setSelectedDataSource,
        setSelectedKpiValues,
        setKpiMetrictsState,
        setGlobalFilters,
        setSimulationDateRange,
        setSimulationOutputDataState,
        setSimulationInputDataState,
        setTitle,
        setSimulationRecommendationDataState,
        setSimulationTimePeriodSelection,
        setIsEditFlowApiCalled,
        setPortfolioUpdateKpisData,
        setBackUpPortfolioUpdateKpisData,
        setPriceEffectiveDate,
        setCreatedByUser,
        isViewMode: isViewMode(),
    });

    // filter CONTEXT
    const {
        allDimAndAttributeTypeList,
        idForRestOfComponent,
        timePeriodLoading,
        timePeriodSelection,
        idForRestOfComponentLoading,
    } = useContext(FilterContextData);

    // reset scenario Id on the first load
    useEffect(() => {
        setScenarioId(0);
        setIsEditFlowApiCalled(false);
    }, []);

    // on change reset date fields
    useEffect(() => {
        if (isCreateMode() || (isEditMode() && isEditFlowApiCalled)) {
            setSimulationDateRange([null, null]);
            setSimulationTimePeriodSelection(null);
        }
    }, [selectedDataSource]);

    useEffect(() => {
        // if by fource non creater tries to edit the scenario, redirecting user to home screen
        if (createdByUser && extractEmailAndName() !== createdByUser && isEditMode()) {
            navigate('/pricing/recommendation/price-simulator');
        }
    }, [createdByUser]);

    // update scenario Id
    useEffect(() => {
        if (id) {
            setScenarioId(Number(id));
            setTriggerFetchScenario(false);
            if (isEditMode()) setIsEditFlowFiltersApplied(false);
        }
    }, [id]);

    // compute has portfolio update kpi changes
    useEffect(() => {
        setHasPortfolioKpisUpdated(!_.isEqual(portfolioUpdateKpisData, backUpPortfolioUpdateKpisData));
    }, [portfolioUpdateKpisData]);

    useEffect(() => {
        if (
            !_.isNil(id) &&
            allDimAndAttributeTypeList &&
            'loading' in allDimAndAttributeTypeList &&
            !allDimAndAttributeTypeList['loading'] &&
            allDimAndAttributeTypeList['dimentions'] &&
            !idForRestOfComponentLoading &&
            !triggertFetchScenario
        ) {
            // when id is present call fetch scenario for View/Edit flow
            setTriggerFetchScenario(true);
            fetchScenario({
                variables: {
                    scenarioId: Number(id),
                },
            });
        }
    }, [
        id,
        idForRestOfComponent,
        idForRestOfComponentLoading,
        allDimAndAttributeTypeList?.loading,
        triggertFetchScenario,
    ]);

    useEffect(() => {
        if (
            allDimAndAttributeTypeList &&
            'loading' in allDimAndAttributeTypeList &&
            !allDimAndAttributeTypeList['loading'] &&
            allDimAndAttributeTypeList['dimentions'] &&
            !idForRestOfComponentLoading &&
            !timePeriodLoading &&
            selectedDataSource.value
        ) {
            if (isCreateMode() || (isEditMode() && isEditFlowApiCalled)) {
                if (isEditMode() && !isEditFlowFiltersApplied) {
                    setIsEditFlowFiltersApplied(true);
                    return;
                }
                fetchKpitMetrics({
                    variables: {
                        filters: JSON.stringify(idForRestOfComponent),
                        timePeriod: simulationTimePeriodSelection
                            ? String(simulationTimePeriodSelection).toLowerCase()
                            : String(timePeriodSelection).toLowerCase(),
                        dataSourceType: selectedDataSource.value,
                    },
                });

                // fetchPortofio update kpis
                fetchPortfolioUpdateKpis({
                    variables: {
                        filters: JSON.stringify(idForRestOfComponent),
                        timePeriod: simulationTimePeriodSelection
                            ? String(simulationTimePeriodSelection).toLowerCase()
                            : String(timePeriodSelection).toLowerCase(),
                        rowAttributeName: levels[0],
                        colAttributeName: levels[1],
                        offset: 0,
                        dataSourceType: selectedDataSource.value,
                    },
                });
            }
        }
    }, [
        idForRestOfComponent,
        idForRestOfComponentLoading,
        allDimAndAttributeTypeList?.loading,
        timePeriodSelection,
        simulationTimePeriodSelection,
    ]);

    const simulatedInputVariables = useMemo(() => {
        if (!idForRestOfComponentLoading && portfolioUpdateKpisData) {
            const filters = JSON.stringify(idForRestOfComponent);
            return {
                filters: filters,
                timePeriod: simulationTimePeriodSelection
                    ? String(simulationTimePeriodSelection).toLowerCase()
                    : String(timePeriodSelection).toLowerCase(),
                attributeNames: levels,
                limit: 500, //need to change
                offset: 0, //need to change
                portfolioUpdateData: reversePortfolioUpdateKpiParser(portfolioUpdateKpisData),
                dataSourceType: selectedDataSource.value,
            };
        }
    }, [
        idForRestOfComponent,
        idForRestOfComponentLoading,
        allDimAndAttributeTypeList?.loading,
        simulationTimePeriodSelection,
        timePeriodSelection,
        portfolioUpdateKpisData,
    ]);

    // call simulation input whenever its create mode or edit mode after edit flow api called
    useEffect(() => {
        if (
            ((isEditMode() && isEditFlowApiCalled) || isCreateMode()) &&
            simulatedInputVariables &&
            backUpPortfolioUpdateKpisData?.length > 0
        ) {
            fetchSimulatorInput({ variables: simulatedInputVariables });
        }
    }, [backUpPortfolioUpdateKpisData]);

    //fetch scenario names
    useEffect(() => {
        if (
            allDimAndAttributeTypeList &&
            'loading' in allDimAndAttributeTypeList &&
            !allDimAndAttributeTypeList['loading'] &&
            allDimAndAttributeTypeList['dimentions'] &&
            !isViewMode()
        ) {
            fetchScenarioNames();
        }
    }, [idForRestOfComponent, allDimAndAttributeTypeList]);

    useEffect(() => {
        if (
            (isCreateMode() || (isEditMode() && isEditFlowApiCalled && isEditFlowFiltersApplied)) &&
            simulationTimePeriodSelection
        ) {
            fetchRecommendationValues({
                variables: {
                    filters: JSON.stringify(idForRestOfComponent),
                    attributeNames: levels,
                    timePeriod: String(simulationTimePeriodSelection).toLowerCase(),
                    dataSourceType: selectedDataSource.value,
                },
            });
        }
    }, [simulationTimePeriodSelection, idForRestOfComponent, allDimAndAttributeTypeList]);

    useEffect(() => {
        setSimulationInputDataState(simulationInputData);
    }, [simulationInputData]);

    useEffect(() => {
        setPortfolioUpdateKpisData(_.cloneDeep(gqlPortfolioUpdateKpis));
        setBackUpPortfolioUpdateKpisData(_.cloneDeep(gqlPortfolioUpdateKpis));
        setExpandedRowKeys([]);
        setExpandedRowDetails({ columnName: '', rowName: '', rowIndex: -1 });
    }, [gqlPortfolioUpdateKpis]);

    useEffect(() => {
        setSimulationRecommendationDataState(recommendationData || []);
    }, [recommendationData]);

    useEffect(() => {
        setSimulationOutputDataState(simulationOutputData);
    }, [simulationOutputData]);

    useEffect(() => {
        if (kpiMetricsData) {
            const newKpiMetrics = kpiMetrictsState.map((item: KpiMetricType) => {
                const actualValue = kpiMetricsData
                    .find((payloadData: any) => payloadData.kpiName === item.value)
                    .kpiValue?.toString();
                return {
                    ...item,
                    actualValue: actualValue,
                    // resetting below fields
                    percentChange: '',
                    goalValue: '',
                    simulatedValue: actualValue,
                };
            });
            setKpiMetrictsState(newKpiMetrics);
        }
    }, [kpiMetricsData]);

    function portfolioUpdateSubmitClick() {
        trackEventMixpanel(PSR_PORTFOLIO_SUBMIT_BTN_CLICKED);
        fetchPortfolioUpdateChangesAppliedKpis({
            variables: {
                filters: JSON.stringify(idForRestOfComponent),
                timePeriod: simulationTimePeriodSelection
                    ? String(simulationTimePeriodSelection).toLowerCase()
                    : String(timePeriodSelection).toLowerCase(),
                rowAttributeName: levels[0],
                colAttributeName: levels[1],
                portfolioUpdateData: reversePortfolioUpdateKpiParser(portfolioUpdateKpisData),
                dataSourceType: selectedDataSource.value,
            },
        });
    }

    function validateFieldsMiddleware(val: number) {
        if (val === 2) {
            if (selectedDataSource.value === '' && !isViewMode()) {
                return { proceed: false, error: 'Please select Data Source' };
            }
            if (simulationDateRange?.includes(null) && !isViewMode()) {
                return { proceed: false, error: 'Please enter Simulation Period' };
            }
            if (_.isNil(id) && scenarioNamesData?.includes(title)) {
                return { proceed: false, error: 'The given scenario name already exists' };
            }
        }
        return { proceed: true, error: null };
    }

    const nextStepHandler = (currentStep: number) => {
        // redirect to home page
        if (currentStep === PriceSimulatorSteps.GO_BACK) {
            navigate('/pricing/recommendation/price-simulator');
        }

        // if its not the last step
        if (currentStep < PriceSimulatorSteps.SIMULATE) {
            //validate fields before proceeding to next step
            const areFieldsValid = validateFieldsMiddleware(currentStep);
            trackEventMixpanel(PSR_NAVIGATION_BUTTON_CLICKED, {
                stepName: PriceSimulatorSteps[currentStep],
            });

            //if validations are not met, show error notification
            if (areFieldsValid?.proceed === false && areFieldsValid?.error) {
                return sendNotification('error', areFieldsValid?.error);
            }

            if (currentStep === PriceSimulatorSteps.PORTFOLIO_UPDATE_STEP) {
                // if user hasn't entered goal values we will update it with actual value(i.e recommended value)
                const newKpimetricdsState: KpiMetricType[] = kpiMetrictsState.map((item: KpiMetricType) => {
                    const newItem: KpiMetricType = { ...item };
                    newItem.goalValue = item.goalValue || item.actualValue;
                    newItem.percentChange = newItem.percentChange || '0';
                    return newItem;
                });

                setKpiMetrictsState(newKpimetricdsState);
            }

            // trigger change portfolio update request when its not view mode
            if (currentStep === PriceSimulatorSteps.IDENTIFY_OPPORTUNITY_STEP && !isViewMode()) {
                // portfolioUpdateSubmitClick();
                if (hasPortfolioKpisUpdated) {
                    sendNotification('error', 'Please submit the Portfolio Update changes to continue');
                    return;
                }
            }

            setCurrentStep(currentStep);
        } else if (currentStep === PriceSimulatorSteps.SIMULATE) {
            trackEventMixpanel(PSR_SIMULATION_BUTTON_CLICKED, {
                stepName: PriceSimulatorSteps[currentStep],
            });
            let priceEffectiveDateUnix = 0; // when user not selected price effective date we will default it to 0
            if (priceEffectiveDate[1]) {
                priceEffectiveDateUnix =
                    dayjs(priceEffectiveDate[1]).add(dayjs().utcOffset(), 'm').subtract(6, 'day').unix() || 0;
            }

            // trigger output
            const variables = {
                inputData: processInputVariablesForOutputCal(
                    clone(simulationInputDataState),
                    levels,
                    reverseObjectKeyValues(inputKeyMapping),
                ),
                attributeNames: levels,
                filters: JSON.stringify(idForRestOfComponent),
                timePeriod: String(simulationTimePeriodSelection).toLowerCase(),
                effectiveTimestamp: priceEffectiveDateUnix,
                portfolioUpdateData: reversePortfolioUpdateKpiParser(portfolioUpdateKpisData),
                dataSourceType: selectedDataSource.value,
            };
            fetchSimulatorOutput({ variables });
            //save scenario
            saveScenario({
                variables: {
                    ...variables,
                    simulatorInputs: variables.inputData,
                    scenarioName: title.trim(),
                    scenarioId: scenarioId, //0 to create a scenario
                    userName: extractEmailAndName(),
                    globalFilter: globalFilters?.map((item) => JSON.stringify(item)),
                    goalInputs: kpiMetrictsState?.map((item) => JSON.stringify(item)),
                    effectiveTimestamp: priceEffectiveDateUnix,
                    portfolioUpdateData: reversePortfolioUpdateKpiParser(portfolioUpdateKpisData),
                    dataSourceType: selectedDataSource.value,
                    presetFilterJson: JSON.stringify({
                        filterObj: {
                            applyFilterObject,
                            allCheckedAttribute,
                            allSelectedValueForAllAttribute,
                        },
                    }),
                },
            });

            // setting title to ouput
            setDisplayTile(tiles[2]);
        }
    };

    return (
        <priceSimulatorDataContext.Provider
            value={{
                scenarioId: scenarioId,
                viewMode: isViewMode(),
                createSimulatorRef,
                currentStep,
                nextStepHandler,
                title,
                setTitle,
                selectedKpiValues,
                setSelectedKpiValues,
                selectedDataSource,
                setSelectedDataSource,
                defaultKpiMetrics,
                kpiMetrictsState,
                setKpiMetrictsState,
                simulationDateRange,
                setSimulationDateRange,
                priceEffectiveDate,
                setPriceEffectiveDate,
                tempPriceEffectiveDate,
                setTempPriceEffectiveDate,
                kpiMetricsLoading,
                kpiMetricsError,
                fetchKpitMetrics,
                simulationInputLoading,
                simulationInputData,
                simulationInputError,
                fetchSimulatorInput,
                levels,
                simulationInputDataState,
                setSimulationInputDataState,
                simulationOutputDataState,
                setSimulationOutputDataState,
                simulationOutputLoading,
                simulationOutputData,
                simulationOutputError,
                fetchSimulatorOutput,
                simulationTimePeriodSelection,
                setSimulationTimePeriodSelection,
                scenarioNamesData,
                globalFilters,
                setGlobalFilters,
                fetchScenario,
                fetchScenarioLoading,
                setDisplayTile,
                displayTile,
                saveScenarioLoading,
                setSimulationRecommendationDataState,
                simulationRecommendationDataState,
                simulationRecomLoading,
                portfolioUpdateKpisData,
                portfolioUpdateKpisLoading,
                setPortfolioUpdateKpisData,
                newPortfolioUpdateKpis,
                setNewPortfolioUpdateKpis,
                expandedRowKeys,
                setExpandedRowKeys,
                expandedRowDetails,
                setExpandedRowDetails,
                addNewPPGDetails,
                setAddNewPPGDetails,
                portfolioUpdateSubmitClick,
                changedPortfolioUpdateKpisLoading,
                hasPortfolioKpisUpdated,
            }}
        >
            {children}
        </priceSimulatorDataContext.Provider>
    );
};

export { priceSimulatorDataContext, PriceSimulatorContext };
